// Étiquetage produit → marque — logique PURE : aucun DOM, aucun réseau.
//
// ── LE PROBLÈME ─────────────────────────────────────────────────────────────
//
// La colonne `laboratoire` des lignes d'achat contient le DISTRIBUTEUR
// (« Alliance Healthcare », « Pierre Fabre Dermo-Cosmétique »), jamais la marque.
// Le titulaire, lui, raisonne en AVENE et en Doliprane. Il n'existe aucune
// source qui donne la marque pour les 10 811 codes produit rencontrés : le
// catalogue marche_para en couvre 35 %, exactement, mais à un niveau inconstant
// (parfois la marque, parfois le groupe). Le reste, personne ne le sait — sauf
// le pharmacien qui a passé les commandes.
//
// Ce module n'essaie donc PAS de deviner. Il organise le travail manuel pour
// qu'il coûte le moins de minutes possible, et il protège ce travail une fois
// fait. Deux règles portent tout le reste :
//
//   1. L'ORDRE (ordonnerGroupes) — 88 groupes de libellés couvrent la moitié des
//      2 774 254 € d'achats, 441 en couvrent 80 %. Étiqueter dans l'ordre
//      alphabétique, ou dans l'ordre d'arrivée, c'est passer la même heure pour
//      un centième du résultat.
//   2. LA PRIORITÉ (fusionner) — une décision humaine ne se fait jamais écraser
//      par un import. Sans cette règle, un re-import du catalogue effacerait
//      silencieusement des heures de travail, et personne ne le verrait avant de
//      chercher pourquoi AVENE est redevenu « Pierre Fabre ».
//
// La clé permanente est le CODE (CIP13/EAN13), pas le libellé : on étiquette un
// groupe de libellés une fois, on propage à ses codes, et toute facture future
// portant ces codes hérite sans nouvelle intervention.

// ── Normalisation et regroupement ───────────────────────────────────────────

/**
 * Clé de regroupement d'un libellé : sans accents, en majuscules, ponctuation et
 * chiffres remplacés par des espaces, PREMIER MOT seulement.
 *
 * Pourquoi le premier mot, et pas quelque chose de plus fin : sur les libellés
 * fournisseurs, la marque est presque toujours en tête (« AVENE Eau thermale
 * 300ML », « KLORANE SHP SEC 150ML »). C'est grossier, ça regroupe 40 682 lignes
 * en 3 263 groupes — soit une taille d'inventaire qu'un humain peut réellement
 * traiter, ce qu'aucun découpage plus fin ne permet.
 *
 * ⚠️ Limite connue et assumée : une élision initiale casse le mot
 * (« L'OREAL » → « L »), donc quelques marques se retrouvent dans un groupe
 * fourre-tout d'une seule lettre. Corriger ça changerait la règle de
 * regroupement — et donc les chiffres de couverture mesurés en base. Le jour où
 * ces groupes gênent, ils se traitent à part, pas en modifiant cette fonction.
 */
export function cleGroupe(designation) {
  const normalise = String(designation ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // accents décomposés par NFD
    .toUpperCase()
    .replace(/[^A-Z]+/g, " ") // ponctuation ET chiffres → séparateurs
    .trim();
  if (!normalise) return "";
  return normalise.split(" ")[0];
}

/**
 * Accepte indifféremment un Set de clés, un tableau de clés, ou un tableau de
 * décisions `{ groupe_cle }`. L'appelant lit ce qu'il a sous la main (la réponse
 * du serveur, ou un état local) ; lui imposer une seule forme obligerait à une
 * conversion à chaque appel, donc à un oubli tôt ou tard.
 */
function ensembleDeCles(source) {
  if (!source) return new Set();
  if (source instanceof Set) return source;
  if (!Array.isArray(source)) return new Set();
  return new Set(
    source
      .map((e) => (typeof e === "string" ? e : (e?.groupe_cle ?? e?.cle)))
      .filter((c) => typeof c === "string"),
  );
}

/**
 * Ordonne la file d'attente d'étiquetage : montant HT décroissant, déjà-décidés
 * retirés.
 *
 * C'est LA fonction qui décide de la valeur d'une soirée de travail. Chaque
 * groupe traité en tête achète bien plus de couverture que dix groupes pris au
 * hasard — la distribution est très concentrée (88 groupes = 50 % des achats).
 *
 * Les départages (nombre de lignes, puis clé alphabétique) ne servent pas à
 * hiérarchiser : ils garantissent que deux affichages successifs de la même
 * liste donnent le même ordre. Une file qui se réordonne toute seule entre deux
 * rendus fait perdre sa place à l'utilisateur.
 */
export function ordonnerGroupes(groupes, dejaEtiquetes) {
  const faits = ensembleDeCles(dejaEtiquetes);
  const nb = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  return (Array.isArray(groupes) ? groupes : [])
    .filter((g) => g && !faits.has(g.cle))
    .sort(
      (a, b) =>
        nb(b.ht) - nb(a.ht) ||
        nb(b.lignes) - nb(a.lignes) ||
        String(a.cle ?? "").localeCompare(String(b.cle ?? "")),
    );
}

// ── Suggestion « ce n'est pas un produit » ──────────────────────────────────

/**
 * Motifs qui trahissent une ligne de facture SANS produit : remise de fin
 * d'année, frais de port, forfait de livraison, opération commerciale.
 *
 * La liste est volontairement serrée autour des familles réellement rencontrées.
 * Un motif trop large (« FRAIS » seul, qui attraperait « effet frais ») coche
 * une case à la place du titulaire sur une ligne qu'il ne relira pas — et une
 * suggestion fausse qu'on ne relit pas devient une décision fausse.
 */
const MOTIFS_NON_PRODUIT = [
  /\bRFA\b/, //                    remise de fin d'année, abrégée
  /\bFIN\s+(D\s+)?ANNEE\b/, //     la même, en toutes lettres (l'apostrophe est déjà un séparateur)
  /\bRISTOURNE\b/,
  /\bESCOMPTE\b/,
  /\bFRAIS\s+(DE\s+)?(PORT|TRANSPORT|LIVRAISON|GESTION|DOSSIER|FACTURATION|EXPEDITION)\b/,
  /\bPORT\b/, //                   « PORT » seul est un libellé courant de facture fournisseur
  /\bFORFAIT\b/,
  /\bLIVRAISON\b/,
  /\bTRANSPORT\b/,
  /\b(CO)?OPERATION\b/, //         « OPERATION GAMME AVENE », « coopération commerciale »
];

/**
 * Ce libellé ressemble-t-il à une ligne sans produit ?
 *
 * SUGGESTION, jamais décision : la valeur retournée pré-coche une case que Mehdi
 * peut décocher. Deviner tout seul serait pire que ne rien deviner — ces lignes
 * pèsent en euros, et les classer à tort ferait disparaître de vrais achats d'un
 * classement par marque sans que rien ne le signale.
 */
export function suggereNonProduit(designation) {
  const s = String(designation ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z]+/g, " ")
    .trim();
  if (!s) return false;
  return MOTIFS_NON_PRODUIT.some((m) => m.test(s));
}

// ── Propagation d'une décision vers les codes ───────────────────────────────

/**
 * Transforme une décision (un groupe) en lignes `produit_marque` (des codes).
 *
 * Un groupe marqué `non_produit` ne produit AUCUNE ligne : c'est tout l'intérêt
 * de la qualification — une RFA n'a pas de marque, et lui en attribuer une
 * gonflerait le total d'achat d'un labo d'un montant qui n'est pas un achat.
 *
 * `maj_le` n'est volontairement pas posé ici : la base a un `default now()`, et
 * fabriquer un horodatage dans une fonction pure rendrait ses tests dépendants
 * de l'heure.
 */
export function propager(decision, codes) {
  if (!decision || decision.non_produit) return [];

  const marque = String(decision.marque ?? "").trim();
  // Sans marque, la base refuserait la ligne (colonne NOT NULL). Mieux vaut ne
  // rien produire que produire une ligne qui fera échouer tout le lot.
  if (!marque) return [];

  const groupeBrut = String(decision.groupe ?? "").trim();
  const groupe = groupeBrut || null;
  const groupeCle = decision.groupe_cle ?? null;

  const vus = new Set();
  const lignes = [];
  for (const brut of Array.isArray(codes) ? codes : []) {
    const code = String(brut ?? "").trim();
    // Un même code peut apparaître sur des dizaines de lignes d'achat du groupe ;
    // `code` est la clé primaire, donc un doublon ferait échouer l'insert entier.
    if (!code || vus.has(code)) continue;
    vus.add(code);
    lignes.push({ code, marque, groupe, origine: "mehdi", groupe_cle: groupeCle });
  }
  return lignes;
}

// ── Priorité entre attributions ─────────────────────────────────────────────

/**
 * Poids d'une origine. Une origine inconnue vaut moins que tout le reste :
 * l'inverse laisserait une source non identifiée écraser une décision humaine.
 */
const PRIORITE_ORIGINE = { mehdi: 2, marche_para: 1 };
const poids = (r) => PRIORITE_ORIGINE[r?.origine] ?? 0;

/**
 * Qui gagne quand un code a déjà une attribution ?
 *
 * ⚠️ C'est le cœur du module. Une décision de Mehdi écrase toujours
 * marche_para ; l'inverse JAMAIS. Sans cette règle, un simple rafraîchissement
 * du catalogue rendrait à AVENE son étiquette « Pierre Fabre » et effacerait des
 * heures d'étiquetage — en silence, et sans que personne ne pense à vérifier.
 *
 * À priorité égale, le nouveau l'emporte : entre deux passages du catalogue,
 * c'est le plus récent qui vaut ; entre deux décisions de Mehdi, la dernière est
 * une correction de la précédente.
 */
export function fusionner(existant, nouveau) {
  if (!existant) return nouveau ?? null;
  if (!nouveau) return existant;
  return poids(nouveau) >= poids(existant) ? nouveau : existant;
}

// ── Progression ─────────────────────────────────────────────────────────────

/**
 * Où en est-on, en montant ET en nombre de groupes.
 *
 * Les deux, parce qu'ils ne racontent pas la même histoire : traiter 88 groupes
 * sur 3 263 (2,7 % de la liste) couvre la moitié des achats. Afficher le seul
 * compteur de groupes donnerait l'impression décourageante — et fausse — de
 * n'avoir rien fait.
 *
 * Aucun plafonnement à 100 % : un pourcentage supérieur ne peut venir que de
 * deux totaux calculés sur des périmètres différents. Le masquer rendrait ce
 * défaut invisible, alors qu'il rendrait tout le tableau de bord faux.
 */
export function progression(couverture) {
  const nb = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const arrondi2 = (v) => Math.round(v * 100) / 100;
  const pourcent = (part, total) => (total > 0 ? Math.round((part / total) * 1000) / 10 : 0);

  const htTotal = nb(couverture?.ht_total);
  const htCouvert = nb(couverture?.ht_couvert);
  const groupesTotal = nb(couverture?.groupes_total);
  const groupesFaits = nb(couverture?.groupes_faits);

  const pctHt = pourcent(htCouvert, htTotal);
  const groupesRestants = Math.max(0, groupesTotal - groupesFaits);

  return {
    ht_total: arrondi2(htTotal),
    ht_couvert: arrondi2(htCouvert),
    ht_restant: arrondi2(htTotal - htCouvert),
    pct_ht: pctHt,
    groupes_total: groupesTotal,
    groupes_faits: groupesFaits,
    groupes_restants: groupesRestants,
    pct_groupes: pourcent(groupesFaits, groupesTotal),
    // Le montant d'abord : c'est la seule des deux mesures qui dise si le
    // référentiel commence à servir à quelque chose.
    resume:
      `${String(pctHt.toFixed(1)).replace(".", ",")} % des achats attribués` +
      (groupesRestants ? ` — ${groupesRestants} groupe${groupesRestants > 1 ? "s" : ""} restant${groupesRestants > 1 ? "s" : ""}` : " — liste terminée"),
  };
}
